import { Component, OnInit } from "@angular/core";
import { StandardService } from "../services/standard.service";

export type RecentUpdatesRange = "5d" | "15d" | "30d" | "custom";

@Component({
  selector: "app-latest-modified-standards",
  templateUrl: "./latest-modified-standards.component.html",
  styleUrls: ["./latest-modified-standards.component.scss"],
})
export class LatestModifiedStandardsComponent implements OnInit {
  allModification: any[] = [];
  latestModification: any[] = [];
  loading: boolean = false;
  // standardTypeId -> route. MarketBestPractices (2) and ABUStandard (7) were
  // previously mis-mapped to the Operations Standard detail page, which expects
  // a different data shape. LocalStandard (3) was pointed at /nrbes-view/,
  // which needs an Nrbes row id, not this standard's own id - routed to the
  // generic standard-id-keyed detail page instead.
  standardRedirectMap: any = {
    1: "/global-recommendations-detail-view/",
    2: "/global-recommendations-detail-view/",
    3: "/view-standard-detail/",
    4: "/minimum-standard-detail-view/",
    5: "/view-equipments-standard-detail/",
    6: "/view-equipments-standard-detail/",
    7: "/view-standard-detail/",
    8: "/operations-standards-detail-view/",
  };

  // standardTypeId -> the matching "New <Type> Standard" icon asset. Types
  // without a dedicated icon (Global Approved Solution, Market Best
  // Practices, Local Standard, BU Standard) fall back to the building icon.
  private typeIconMap: { [id: number]: string } = {
    4: "/assets/images/Property 1=New Building Standard.svg",
    5: "/assets/images/Property 1=New Equipment Standard.svg",
    6: "/assets/images/Property 1=New Equipment Standard.svg",
    8: "/assets/images/Property 1=New Operations Standard.svg",
  };

  getRecordIcon(record: any): string {
    if (record.badgeClass === "badge-obsolete") {
      return "/assets/images/Property 1=Obsolete Equipment.svg";
    }
    return (
      this.typeIconMap[record.standardTypeId] ||
      "/assets/images/Property 1=New Building Standard.svg"
    );
  }

  // "custom" only becomes the active range once Apply is pressed in the
  // popover - picking "Custom" from the segmented control just opens the
  // popover without changing the active filter yet.
  selectedRange: RecentUpdatesRange = "5d";
  customStartDate: Date;
  customEndDate: Date;

  showCustomPopover: boolean = false;
  draftStartDate: Date;
  draftEndDate: Date;

  constructor(public standardService: StandardService) {}

  get isCustomApplied(): boolean {
    return (
      this.selectedRange === "custom" &&
      !!this.customStartDate &&
      !!this.customEndDate
    );
  }

  selectRange(range: "5d" | "15d" | "30d"): void {
    this.selectedRange = range;
    this.customStartDate = null;
    this.customEndDate = null;
    this.showCustomPopover = false;
    this.applyRangeFilter();
  }

  openCustomPopover(): void {
    const today = new Date();
    const monthAgo = new Date();
    monthAgo.setDate(today.getDate() - 30);
    this.draftStartDate = this.customStartDate || monthAgo;
    this.draftEndDate = this.customEndDate || today;
    this.showCustomPopover = true;
  }

  cancelCustomPopover(): void {
    this.showCustomPopover = false;
  }

  applyCustomPopover(): void {
    if (!this.draftStartDate || !this.draftEndDate) return;
    this.customStartDate = this.draftStartDate;
    this.customEndDate = this.draftEndDate;
    this.selectedRange = "custom";
    this.showCustomPopover = false;
    this.applyRangeFilter();
  }

  resetRange(): void {
    this.customStartDate = null;
    this.customEndDate = null;
    this.selectedRange = "5d";
    this.applyRangeFilter();
  }

  private formatShortDate(date: Date): string {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  }

  get customRangeLabel(): string {
    if (!this.customStartDate || !this.customEndDate) return "";
    return `${this.formatShortDate(this.customStartDate)} - ${this.formatShortDate(
      this.customEndDate
    )}`;
  }

  get customDaysCount(): number {
    if (!this.customStartDate || !this.customEndDate) return 0;
    const from = new Date(this.customStartDate);
    const to = new Date(this.customEndDate);
    const msPerDay = 24 * 60 * 60 * 1000;
    return (
      Math.round(
        (Date.UTC(to.getFullYear(), to.getMonth(), to.getDate()) -
          Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())) /
          msPerDay
      ) + 1
    );
  }

  private applyRangeFilter(): void {
    let fromDate: Date;
    let toDate: Date = new Date();
    toDate.setHours(23, 59, 59, 999);

    if (this.selectedRange === "custom") {
      if (!this.customStartDate || !this.customEndDate) {
        this.latestModification = this.allModification;
        return;
      }
      fromDate = new Date(this.customStartDate.getTime());
      fromDate.setHours(0, 0, 0, 0);
      toDate = new Date(this.customEndDate.getTime());
      toDate.setHours(23, 59, 59, 999);
    } else {
      const days =
        this.selectedRange === "5d" ? 5 : this.selectedRange === "15d" ? 15 : 30;
      fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - days);
      fromDate.setHours(0, 0, 0, 0);
    }

    this.latestModification = this.allModification.filter(
      (record) => record.date >= fromDate && record.date <= toDate
    );
  }

  ngOnInit() {
    this.loading = true;
    this.standardService
      .getLatestModifiedStandards()
      .subscribe((response: any) => {
        this.allModification = [];
        for (let standard of response.standardDetailList) {
          const now = new Date();
          const isUpcoming =
            (standard.effectiveFrom && new Date(standard.effectiveFrom) > now) ||
            (standard.newRestaurantsEffectiveFrom &&
              new Date(standard.newRestaurantsEffectiveFrom) > now);
          const isObsolete = standard.isActive === false && !isUpcoming;
          const isUpdated = !!standard.modifiedUser;
          const badgeLabel = isObsolete ? "Obsolete" : isUpdated ? "Updated" : "Added";
          const badgeClass = isObsolete
            ? "badge-obsolete"
            : isUpdated
            ? "badge-updated"
            : "badge-added";
          this.allModification.push({
            date: new Date(standard.lastModifiedDate),
            badgeLabel: badgeLabel,
            badgeClass: badgeClass,
            description:
              standard.description.length <= 100
                ? standard.description
                : standard.description.substring(0, 100) + "...",
            title: standard.title,
            creator:
              standard.createdUser.name + " " + standard.createdUser.lastName,
            role: standard.createdUser.userRole.name,
            standardId: standard.id,
            standardTypeId: standard.standardTypeId,
            isUpcoming: standard.isUpcoming,
            redirectUrl: standard.isUpcoming
              ? "/view-upcoming-standard-detail/" + standard.id
              : this.standardRedirectMap[standard.standardTypeId] + standard.id,
          });
        }
        this.applyRangeFilter();
        this.loading = false;

        try {
          const events = this.allModification.map((m) => ({
            ...m,
            eventType: "GRSRecentUpdatesPROD",
            session: JSON.parse(localStorage.getItem("NRBA_SESSION")).value,
          }));
          this.standardService.recordEvent(events).subscribe({
            next: () => void 0,
            error: () => void 0,
          });
        } catch (e) {
          void 0;
        }
      });
  }
}
